<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import mainViewTabMixin from '../../mixins/mainViewTab';
import paginationMixin from '../../mixins/pagination';
import { employeeListHeaders } from './config';
import EmployeeListItem from './components/EmployeeListItem';
import NewEmployeeDialog from './components/NewEmployeeDialog';
import CsvImportDialog from './components/CsvImportDialog';
import CsvExportDialog from './components/CsvExportDialog';
import RbxConfirmDialog from '../../shared/reybex/RbxConfirmDialog';

export default {
  name: 'EmployeeList',
  components: {
    EmployeeListItem,
    NewEmployeeDialog,
    RbxConfirmDialog,
    CsvImportDialog,
    CsvExportDialog,
  },
  mixins: [mainViewTabMixin, paginationMixin],
  data() {
    return {
      isInitialized: false,
      headers: employeeListHeaders,
      showNewEmployeeDialog: false,
      isConfirmDialogDisplayed: false,
      isCsvImportDialogDisplayed: false,
      isCsvExportDialogDisplayed: false,
      confirmDeletionMessage: '',
      employeeToDelete: {},
      isDeleteFilter: false,
      isDeleteOptions: [{ id: null, text: 'alle' }, { id: true, text: 'ja' }, { id: false, text: 'nein' }],
      searchTerm: '',
    };
  },
  computed: {
    ...mapState(['employeeList']),
    ...mapState('employeeList', ['data', 'meta', 'isLoading', 'hasError']),
    ...mapGetters('app', ['auth']),
    filteredItems() {
      if (!this.data) return [];
      if (this.isDeleteFilter == null) return this.data;

      return this.data.filter(it => it.isDelete === this.isDeleteFilter);
    },
  },
  mounted() {
    this.fetchData(this.paginationParams).then(() => {
      this.isInitialized = true;
    });
  },
  methods: {
    ...mapActions('employeeList', { fetchData: 'fetchEmployeeList' }),
    ...mapActions('employeeList', { deleteEmployee: 'deleteEmployee' }),
    onDelete(employeeToDelete) {
      this.employeeToDelete = employeeToDelete;
      this.confirmDeletionMessage =
        `Soll der Mitarbeiter ${this.employeeToDelete.name} gelöscht werden?`;
      this.isConfirmDialogDisplayed = true;
    },
    onRefuse() {
      this.isConfirmDialogDisplayed = false;
    },
    onConfirm() {
      this.deleteEmployee(this.employeeToDelete)
        .then(() => {
          this.fetchData(this.paginationParams).then(() => {
            this.isInitialized = true;
          });
          this.isConfirmDialogDisplayed = false;
        });
    },
    refresh() {
      this.fetchData(this.paginationParams);
    },
    onCsvImport() {
      this.isCsvImportDialogDisplayed = true;
    },
    onCsvExport() {
      this.isCsvExportDialogDisplayed = true;
    },
    onSearch() {
      this.paginationParams.searchTerm = this.searchTerm;
      this.fetchData(this.paginationParams);
    },
  },
};
</script>

<template>
  <div class="employee-list">
    <template v-if="employeeList.hasError">
      <p class="js-error-template ma-3">Failed to fetch employee list. Please try again!</p>
    </template>
    <template v-else>
      <v-toolbar dark dense height="40" color="blue-grey lighten-2">
        <v-tooltip bottom>
          <v-btn icon slot="activator" @click="showNewEmployeeDialog = true">
            <v-icon>add</v-icon>
          </v-btn>
          <span>Hinzufügen</span>
        </v-tooltip>

        <v-tooltip bottom>
          <v-btn @click="refresh" icon slot="activator">
            <v-icon>refresh</v-icon>
          </v-btn>
          <span>Aktualisieren</span>
        </v-tooltip>

        <v-tooltip bottom>
          <v-btn @click="onCsvImport" icon slot="activator">
            <v-icon>cloud_upload</v-icon>
          </v-btn>
          <span>CSV-Import</span>
        </v-tooltip>

        <v-tooltip bottom>
          <v-btn v-if="auth.hasDownloadRights" @click="onCsvExport" icon slot="activator">
            <v-icon>cloud_download</v-icon>
          </v-btn>
          <span>CSV-Export</span>
        </v-tooltip>

        <v-spacer />
        <v-flex md3 >
          <v-text-field
            v-model="searchTerm"
            label="Suche"
            height="22"
            class="caption"
            @keyup.enter="onSearch"
            @blur="onSearch"
          />
        </v-flex>
      </v-toolbar>

      <v-flex xs4 md2>
        <v-select
          style="padding-left: 22px; padding-top: 25px;"
          :items="isDeleteOptions"
          v-model="isDeleteFilter"
          item-value="id"
          item-text="text"
          label="Gelöscht" />
      </v-flex>

      <v-progress-linear
        v-if="employeeList.isLoading && !isInitialized"
        :indeterminate="true"
        class="js-loading ma-0"
      />

      <v-data-table
        v-else
        dense
        :headers="headers"
        :items="filteredItems"
        :disable-initial-sort="true"
        :pagination.sync="pagination"
        :loading="employeeList.isLoading"
        :rows-per-page-items="rowsPerPageItems"
        :total-items="pagination.totalItems"
        :expand="true"
        item-key="id"
        @update:pagination="loadNextPage"
        rows-per-page-text="Zeilen pro Seite"
        no-data-text="keine Daten verfügbar"
        class="elevation-1 js-table"
      >
        <template slot="headers" slot-scope="props">
          <tr>
            <th
              v-for="header in props.headers"
              :key="header.text"
              :class="['column sortable',
                       pagination.descending ? 'desc' : 'asc',
                       header.value === pagination.sortBy ? 'active' : '']"
              @click="changeSort(header.value, filteredItems)"
            >
              <v-icon small>arrow_upward</v-icon>
              {{ header.text }}
            </th>
          </tr>
        </template>

        <template slot="items" slot-scope="props">
          <employee-list-item
            :employee="props.item"
            :expanded="props.expanded"
            @delete="onDelete"
            @refreshList="refresh"
          />
        </template>

        <template slot="pageText" slot-scope="{ pageStart, pageStop }">
          von {{ pageStart }} bis {{ pageStop }}
        </template>
      </v-data-table>

      <NewEmployeeDialog
        v-if="showNewEmployeeDialog"
        @cancel="showNewEmployeeDialog = false"
      />

      <csv-import-dialog
        v-if="isCsvImportDialogDisplayed"
        @cancel="isCsvImportDialogDisplayed = false"
        @imported="refresh"
      />

      <csv-export-dialog
        v-if="isCsvExportDialogDisplayed"
        @cancel="isCsvExportDialogDisplayed = false"
      />

      <rbx-confirm-dialog @confirm="onConfirm" @refuse="onRefuse"
                          :message="confirmDeletionMessage"
                          :is-displayed="isConfirmDialogDisplayed" />

    </template>
  </div>
</template>

<style lang="scss" scoped>
.variant-cell {
  width: 30px;

  i {
    cursor: pointer;
  }
}
</style>
